import { GatsbyLinkProps, Link } from 'gatsby'
import React, { ReactHTML } from 'react'
import styled from 'styled-components'
import * as StyleProps from '../../styles/stylePropTypes'
import * as styleProps from '../../styles/styleProps'
import {
  StyledComponentProps,
  styledComponentConfig,
} from '../../styles/styledComponents'

export type BoxStyleProps = StyleProps.CustomCss &
  StyleProps.Background &
  StyleProps.Border &
  StyleProps.Color &
  StyleProps.Flexbox &
  StyleProps.Grid &
  StyleProps.Layout &
  StyleProps.Position &
  StyleProps.Shadow &
  StyleProps.Space &
  StyleProps.Typography &
  StyleProps.FontVariant &
  StyleProps.Misc &
  StyleProps.Truncation

export type BoxProps<HtmlTag extends keyof ReactHTML = 'div'> =
  // This gives us all the props a regular styled-component would have:
  StyledComponentProps<HtmlTag> &
    // This gives us the style props we want:
    BoxStyleProps

const boxProps = [
  styleProps.fontVariant,
  styleProps.truncation,
  styleProps.autoBorder,
  styleProps.background,
  styleProps.border,
  styleProps.color,
  styleProps.flexbox,
  styleProps.grid,
  styleProps.layout,
  styleProps.position,
  styleProps.shadow,
  styleProps.space,
  styleProps.typography,
  styleProps.misc,
]

/**
 * Produces <Box> constructors for different HTML tags.
 *
 * Regular components based on styled-system don't need such complex construction.
 */
function makeBox<HtmlTag extends keyof ReactHTML>(
  element: HtmlTag,
): React.FC<BoxProps<HtmlTag>> {
  return Object.assign(
    styled(element).withConfig(styledComponentConfig)(
      {}, // no direct CSS properties needed
      ...boxProps,
    ),
    { displayName: `Box.${element}` }, // set a more useful displayName for the React component
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any // TODO: The prop types of styled() are more permissive than what we want for <Box>, but also not exactly compatible -> need to force cast via "any"
}

const Box = makeBox('div')

export default Object.assign(Box, {
  a: makeBox('a'),
  article: makeBox('article'),
  button: makeBox('button'),
  div: makeBox('div'),
  form: makeBox('form'),
  h1: makeBox('h1'),
  h2: makeBox('h2'),
  h3: makeBox('h3'),
  h4: makeBox('h4'),
  h5: makeBox('h5'),
  h6: makeBox('h6'),
  hr: makeBox('hr'),
  img: makeBox('img'),
  input: makeBox('input'),
  br: makeBox('br'),
  label: makeBox('label'),
  li: makeBox('li'),
  ol: makeBox('ol'),
  optgroup: makeBox('optgroup'),
  option: makeBox('option'),
  p: makeBox('p'),
  section: makeBox('section'),
  select: makeBox('select'),
  span: makeBox('span'),
  table: makeBox('table'),
  tbody: makeBox('tbody'),
  td: makeBox('td'),
  textarea: makeBox('textarea'),
  tfoot: makeBox('tfoot'),
  th: makeBox('th'),
  thead: makeBox('thead'),
  tr: makeBox('tr'),
  ul: makeBox('ul'),
  pre: makeBox('pre'),
  iframe: makeBox('iframe'),
  blockquote: makeBox('blockquote'),
  details: makeBox('details'),
  summary: makeBox('summary'),
  // reach-router that Gatsby uses is a bit bad, so we do this hack
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link: makeBox(Link as any) as React.FC<BoxProps<'a'> & GatsbyLinkProps<any>>,
})
